import { Trash } from '@griply/assets';
import { useArchiveLifeArea } from 'features/life-areas';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArchiveDocumentsBoxBig16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenDeleteLifeAreaDialog } from 'shared/contexts/delete-life-area';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useUser } from 'shared/hooks/use-user';
import { LifeArea } from 'shared/types/life-area';
import { LimitKeys } from 'shared/types/limit-keys';

export type LifeAreaMultiSelectContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref'
> & {
  lifeAreas: LifeArea[];
  onClose: () => void;
};

export const LifeAreaMultiSelectContextMenu: React.FC<
  LifeAreaMultiSelectContextMenuProps
> = ({ lifeAreas, onClose, location, position, referenceElement }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const user = useUser();

  const archiveLifeArea = useArchiveLifeArea();
  const openDeleteDialog = useOpenDeleteLifeAreaDialog();
  const openPremiumDialog = useOpenPremiumDialog();
  const { allowed: editLifeAreasAllowed } = usePremiumFeatureAllowed(
    LimitKeys.EditLifeAreas,
    { user },
  );

  const allArchived = useMemo(
    () => lifeAreas.every(({ archivedAt }) => !!archivedAt),
    [lifeAreas],
  );

  // useArchiveLifeArea toggles, so a mixed selection must flip only one side.
  const onArchive = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      lifeAreas
        .filter(({ archivedAt }) => (allArchived ? !!archivedAt : !archivedAt))
        .forEach(archiveLifeArea);
      onClose();
    },
    [allArchived, archiveLifeArea, lifeAreas, onClose],
  );

  const onDelete = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (!editLifeAreasAllowed) {
        openPremiumDialog();
        onClose();
        return;
      }

      openDeleteDialog(lifeAreas);
      onClose();
    },
    [
      editLifeAreasAllowed,
      lifeAreas,
      onClose,
      openDeleteDialog,
      openPremiumDialog,
    ],
  );

  return (
    <PopupMenu
      ref={containerRef}
      location={location}
      position={position}
      referenceElement={referenceElement}
      onOutside={onClose}
    >
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onArchive}
            start={<Icon icon={ArchiveDocumentsBoxBig16} />}
          >
            {t(
              allArchived
                ? 'context-menus.life-area.labels.de-archive'
                : 'context-menus.life-area.labels.archive',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDelete}
            start={<Icon icon={Trash} />}
            startColor={Colors.Error}
            textColor={Colors.Error}
            isPremium={!editLifeAreasAllowed}
          >
            {t('context-menus.life-area.labels.delete')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
